/*
  Bello — "Build Your Own" buy box, cart adapter (A/B Variant B, Test 12).

  Turns a buy box state into Shopify cart lines. Split in two halves on purpose:

    buildCartLines(state, catalog)  pure, testable, no DOM and no network
    addToCart(lines, options)       the POST, kept as thin as it can be

  Line shape follows decision #1 in docs/test-12-build-your-own-buy-box.md:
  one line for the pack, one line per flavour bought on top of what the pack covers.
  The capsules the pack already covers are NOT lines — they ride the pack line as
  properties, because they are not a separate charge.

  `packShipsCapsules: false` inverts that: the pack product carries no capsules of its
  own, so every capsule the shopper picked becomes a line and the pack line stops
  needing a property to describe them. The order then states what to ship instead of
  asking the warehouse to read a note. See "Unbundled packs" in the doc.

  Money never appears here. getTotal() in the engine is the only price authority and
  Shopify re-prices everything server-side anyway; a cents value in this file could
  only ever disagree with one of them.

  UNRESOLVED — three inputs are still waiting on the client, and each one is isolated
  behind a single named seam so wiring them up is a one-line change:

    machineIncludesCapsule   Starter Pack ships with 1 Lemon Ginger inside, but the
                             spec charges every capsule on Machine + Filter. Left as
                             false (= spec) until fulfillment confirms; flipping it to
                             true drops one capsule from the order. See risk 1.
    filterSubscription       Needs product + selling plan + cadence. Two live products
                             exist, one per cadence, and the spec's 300-day tier has no
                             product at all. Returns no line until configured. Risks 2/3.
    capsuleSubscription      The plans exist in Recharge, but the CTA quotes today's
                             charge only and the toggle must not move it — so the refill
                             has to bill on a later date, the same deferred-first-charge
                             problem the filter has. Priced now it would double the
                             capsules in the first order. No line until confirmed.
*/
(function (global) {
  'use strict';

  /* Resolved per call, not captured at load: the engine registers itself on the same
     global, and nothing guarantees which of the two scripts the browser runs first. */
  function resolveEngine(options) {
    var E = (options && options.engine) || global.BelloBuyBoxEngine;
    if (!E) throw new Error('BelloBuyBoxEngine is not loaded');
    return E;
  }

  /* How many capsules the pack product physically ships. `pack.capsules` is what the flat
     price covers, which is a pricing rule and stays untouched — this is a fact about the
     product, and the two only coincide while the packs are bundles with capsules inside. */
  function shippedByPack(pack, options) {
    return options && options.packShipsCapsules === false ? 0 : pack.capsules;
  }

  /* Included slots are filled in catalog order, and whatever is left over becomes the
     extra lines. Every capsule costs the same, so the split never moves the total — but
     it does decide which flavours appear as properties and which as their own lines, and
     that has to be stable across renders. Catalog order is the same tie-break the bundle
     fill uses.

     With unbundled packs nothing is covered, so `included` comes back empty and every
     capsule is its own line — which is already how Machine + Filter has always worked. */
  function splitCapsules(state, options) {
    var opts = options || {};
    var E = resolveEngine(opts);
    var pack = E.findPack(state.packId);
    var covered = shippedByPack(pack, opts);
    /* Both sources merged: the steppers and the selected preset are kept apart on screen
       so the shopper can tell one from the other, but the order only cares about the
       capsules themselves. Merged per flavour, so a preset's Lemon Ginger and a stepped-up
       one leave as a single line of the right quantity. */
    var mix = E.selectedMix(state);

    var included = [];
    var extra = [];

    E.FLAVOURS.forEach(function (flavour) {
      var n = mix[flavour.id];
      if (!n) return;

      var take = Math.min(n, covered);
      covered -= take;
      if (take > 0) included.push({ id: flavour.id, name: flavour.name, count: take });
      if (n - take > 0) extra.push({ id: flavour.id, name: flavour.name, count: n - take });
    });

    return { included: included, extra: extra };
  }

  /* "2× Lemon Ginger, 1× Apple Kiwi" — one property value rather than one property per
     flavour, so the line reads as a single legible row in the admin and on the packing
     slip. Confirm with fulfillment before launch (risk 6). */
  function describeMix(entries) {
    return entries
      .map(function (entry) {
        return entry.count + '× ' + entry.name;
      })
      .join(', ');
  }

  function variantIdOf(item) {
    var id = item && item.variantId;
    return id ? Number(id) : 0;
  }

  /* state -> lines, in the order Shopify should receive them: pack first, then extras.

     Throws on a missing variant id instead of posting a partial order. A pack with no
     resolvable product is a broken catalog, and half an order reaching the cart is worse
     than a CTA that reports it could not add. Same for a sold-out flavour: the engine
     already refuses to add one, so reaching here means the catalog changed under a build
     in progress, and Shopify would reject the whole post anyway. */
  function buildCartLines(state, options) {
    var opts = options || {};
    var E = resolveEngine(opts);
    var pack = E.findPack(state.packId);
    var split = splitCapsules(state, opts);
    var lines = [];

    /* Over the whole build, presets included — the engine refuses to step a sold-out
       flavour up, but a preset carries its own flavours and stock can move under a build
       in progress either way. */
    Object.keys(E.selectedMix(state)).forEach(function (id) {
      if (!E.isSellable(E.findFlavour(id))) {
        throw new Error('Flavour "' + id + '" is out of stock');
      }
    });

    var packVariant = variantIdOf(pack);
    if (!packVariant) throw new Error('No variant id for pack "' + pack.id + '"');

    var packLine = {
      id: packVariant,
      quantity: 1,
      /* Yuri's grouping key (2026-09-04): every buy-box line carries `_pmd_bundle` so the
         cart can collapse them under one build. The leading underscore hides it from the
         checkout. The value is the line's role, which is all the cart needs to place it. */
      properties: { _pmd_bundle: 'main' }
    };

    if (split.included.length) {
      packLine.properties['Capsules included'] = describeMix(split.included);
    }

    /* Starter Pack already ships one Lemon Ginger. Until the client confirms whether the
       shopper's first capsule replaces it or adds to it, we follow the spec and charge
       every capsule — the flag is the whole of the change when the answer arrives. */
    if (pack.id === 'machine' && opts.machineIncludesCapsule) {
      var first = split.extra[0];
      if (first) {
        if (first.count > 1) {
          first.count -= 1;
        } else {
          split.extra.shift();
        }
        packLine.properties['Capsule in the box'] = first.name;
      }
    }

    lines.push(packLine);

    split.extra.forEach(function (entry) {
      var flavour = E.FLAVOURS.filter(function (f) {
        return f.id === entry.id;
      })[0];
      var variant = variantIdOf(flavour);
      if (!variant) throw new Error('No variant id for flavour "' + entry.id + '"');
      lines.push({ id: variant, quantity: entry.count, properties: { _pmd_bundle: 'capsule' } });
    });

    var filter = filterSubscriptionLine(state, opts);
    if (filter) lines.push(filter);

    capsuleSubscriptionLines(state, opts).forEach(function (line) {
      lines.push(line);
    });

    return lines;
  }

  /* BLOCKED — risks 2 and 3. Production runs one product per cadence, each with its own
     Recharge selling plan, and the spec's 300-day tier matches no existing product. Until
     the pairing is confirmed the toggle simply adds nothing, which keeps the rest of the
     order correct instead of guessing a plan id.

     `options.filterSubs` is that pairing: { machine: {variantId, sellingPlanId}, ... }. */
  function filterSubscriptionLine(state, options) {
    if (!state.isFilterSub) return null;
    var config = options.filterSubs && options.filterSubs[state.packId];
    if (!config || !config.variantId || !config.sellingPlanId) return null;

    return {
      id: Number(config.variantId),
      quantity: 1,
      selling_plan: Number(config.sellingPlanId),
      properties: { _pmd_bundle: 'filter' }
    };
  }

  /* BLOCKED — the refill is a future order, not part of today's charge: the CTA quotes
     the same total whether the toggle is on or off. Priced on the spot these lines would
     bill the shopper for a second set of capsules, so they need a plan that defers its
     first billing date — the same unanswered question as the filter.

     The mapping itself is settled: one line per flavour over the shopper's whole
     selection, on that flavour's plan for the chosen frequency. `options.capsuleSubs` is
     the missing half, a flavourId -> { weeks: sellingPlanId } map; without it nothing is
     emitted.

     The frequency has to reach the line, not just the copy: Recharge prints the plan's own
     name in the cart and at checkout, so a 6-week choice riding on the monthly plan reads
     back to the shopper as "every month" (Ammiel, 2026-09-11). A flavour with no plan at
     the chosen frequency is skipped rather than billed on a neighbouring cadence. */
  /* A flavour's plans arrive keyed by week count. A bare number is still accepted so a
     store that has only ever had one capsule plan keeps working, but it is deliberately
     NOT used as a fallback when the keyed lookup misses — landing on the wrong cadence is
     worse than emitting no subscription at all. */
  function planForFrequency(plans, weeks) {
    if (!plans) return null;
    if (typeof plans === 'number' || typeof plans === 'string') return plans;
    return plans[weeks] || plans[String(weeks)] || null;
  }

  function capsuleSubscriptionLines(state, options) {
    if (!state.isCapSub || !options.capsuleSubs) return [];
    var E = resolveEngine(options);
    var mix = E.getCapsuleSubscription(state).mix;
    var lines = [];

    E.FLAVOURS.forEach(function (flavour) {
      var quantity = mix[flavour.id];
      var plan = planForFrequency(options.capsuleSubs[flavour.id], state.capFreqWeeks);
      if (!quantity || !plan || !flavour.variantId) return;
      lines.push({
        id: Number(flavour.variantId),
        quantity: quantity,
        selling_plan: Number(plan),
        properties: { _pmd_bundle: 'capsule-sub' }
      });
    });

    return lines;
  }

  /* ---- the POST -------------------------------------------------------------
     /cart/add takes every line in one request, so the order is atomic: either the whole
     build lands in the cart or none of it does. Adding line by line would leave a partial
     build behind on the first failure.

     `localeRoot` matters: the store is EN/FR and posting to the wrong locale root drops
     the shopper out of their language at checkout. */
  function addToCart(lines, options) {
    var root = (options && options.localeRoot) || '/';

    return fetch(root.replace(/\/$/, '') + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines }),
      credentials: 'same-origin'
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          throw new Error(data && data.description ? data.description : 'Add to cart failed');
        }
        return data;
      });
    });
  }

  var api = {
    splitCapsules: splitCapsules,
    shippedByPack: shippedByPack,
    describeMix: describeMix,
    buildCartLines: buildCartLines,
    addToCart: addToCart
  };

  global.BelloBuyBoxCart = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
